import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [loading, setLoading] = useState(false);

  const onPwKey = (e) => {
    if (typeof e.getModifierState === "function") setCapsOn(e.getModifierState("CapsLock"));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/customer/reset-password", { email: email.trim().toLowerCase(), new_password: password });
      toast.success("Password updated — signing you in.");
      navigate("/signin");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) toast.error("No account exists for that email.");
      else if (status === 429) toast.error("Too many attempts. Please wait a few minutes.");
      else toast.error(err?.response?.data?.detail || "Couldn't reset your password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Reset password" />
      <section className="sign-up-section" data-testid="reset-hero" style={{ minHeight: "70vh" }}>
        <div className="sign-up-container" style={{ maxWidth: 560 }}>
          <div className="sign-up-form-div-wrapper" style={{ width: "100%" }}>
            <div className="sign-up-form-wrapper" style={{ width: "100%", maxWidth: 520, marginLeft: "auto", marginRight: "auto" }}>
              <div style={{ textAlign: "center", marginBottom: 26 }}>
                <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 18 }}>
                  Account recovery
                </div>
                <h1 className="h2 _700-bold">Reset your password</h1>
                <p style={{ color: "#6c6376", marginTop: 12, fontSize: 15 }}>Type the email you signed up with and pick a new password. You'll be able to sign in straight away.</p>
              </div>

              <form
                onSubmit={onSubmit}
                data-testid="reset-password-form"
                style={{ background: "#fff", padding: 34, borderRadius: 24, border: "1px solid #eaeaf0", boxShadow: "0 24px 60px -30px rgba(41,45,85,.25)" }}
              >
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Email</label>
                  <input data-testid="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" placeholder="you@example.com" style={inputStyle} />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>New password</label>
                  <div style={{ position: "relative" }}>
                    <input data-testid="reset-password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={onPwKey} onKeyUp={onPwKey} required minLength={8} autoComplete="new-password" placeholder="At least 8 characters" style={{ ...inputStyle, paddingRight: 60 }} />
                    <button type="button" onClick={() => setShow((v) => !v)} style={showBtn}>{show ? "Hide" : "Show"}</button>
                  </div>
                  {capsOn && <div style={{ fontSize: 12, color: "#b45309", marginTop: 6, fontWeight: 700 }}>⚠️ Caps Lock is ON</div>}
                </div>

                <div style={{ marginBottom: 22 }}>
                  <label style={labelStyle}>Confirm new password</label>
                  <input
                    data-testid="reset-password-confirm"
                    type={show ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    onKeyDown={onPwKey}
                    onKeyUp={onPwKey}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    placeholder="Type it again"
                    style={{ ...inputStyle, borderColor: confirm && confirm !== password ? "#dc2626" : "#d8dcec" }}
                  />
                  {confirm && confirm !== password && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>Passwords don't match yet.</div>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit"
                  className="primary-button w-button"
                  style={{ width: "100%", border: "none", cursor: loading ? "wait" : "pointer", opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? "Updating…" : "Reset password"}
                </button>

                <p style={{ marginTop: 18, textAlign: "center", fontSize: 12.5, color: "#6c6376" }}>
                  <Link to="/signin" style={{ color: "#346af8", fontWeight: 700 }}>← Back to sign in</Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#0b0d17", marginBottom: 8 };
const inputStyle = { width: "100%", padding: "13px 16px", borderRadius: 12, border: "1px solid #d8dcec", background: "#fff", color: "#0b0d17", fontSize: 15, outline: "none" };
const showBtn = { position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", fontSize: 12, fontWeight: 600, color: "#6c6376", cursor: "pointer" };
